import { useEffect, useState } from "react";
import { BrowserRouter } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { I18nextProvider } from "react-i18next";

import { ErrorBoundary } from "@/app/ErrorBoundary";
import { AdminRoutes } from "./routes";
import { FullScreenLoader } from "@/components/FullScreenLoader";
import { queryClient } from "@/api/queryClient";
import { adminWebAuthenticate } from "@/api/adminWebAuth";
import { i18n, initI18n } from "@/i18n";
import { useSessionStore } from "@/store/session";

/** DUE-221 (Task 12): boot flow for the standalone browser admin. Unlike
 * `App.tsx`'s `ensureSession()` (Telegram initData / iOS device-guest),
 * this bundle has no ambient identity to authenticate with — it only ever
 * has a previously-issued refresh token (persisted by
 * `exchangeAdminLoginCode`, Task 10) or nothing at all. `ready` gates
 * mounting `<AdminRoutes />` so `RequireAdminWeb` never sees a moment where
 * the store is hydrated but the refresh-token attempt hasn't settled yet. */
function useAdminBoot(): boolean {
  const [ready, setReady] = useState(false);
  const hydrateFromStorage = useSessionStore((s) => s.hydrateFromStorage);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      await hydrateFromStorage();
      if (cancelled) return;
      if (!useSessionStore.getState().jwt) {
        try {
          const envelope = await adminWebAuthenticate();
          useSessionStore.getState().setSession(envelope);
        } catch {
          // No/dead refresh token — RequireAdminWeb sends us to /login.
        }
      }
      if (!cancelled) setReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [hydrateFromStorage]);
  return ready;
}

function Bootstrap(): JSX.Element {
  const ready = useAdminBoot();
  if (!ready) return <FullScreenLoader />;
  // index.css locks <html>/<body> scrolling (MAV-110) — `.app-scroll` is
  // the app's single scrolling surface, same as App.tsx's Bootstrap.
  // Without this wrapper the admin SPA renders taller than the viewport
  // with no way to scroll.
  return (
    <div className="app-scroll admin-scroll">
      <AdminRoutes />
    </div>
  );
}

export function AdminApp(): JSX.Element {
  const [i18nReady, setI18nReady] = useState(i18n.isInitialized);
  const userLocale = useSessionStore((state) => state.user?.locale);

  useEffect(() => {
    void initI18n().then(() => setI18nReady(true));
  }, []);

  // MAV-114: same rationale as App.tsx — the backend is the source of
  // truth for UI language, so we boot in English and swap once the
  // session (and its `user.locale`) lands.
  useEffect(() => {
    if (!i18nReady || !userLocale) return;
    if (i18n.language === userLocale) return;
    void i18n.changeLanguage(userLocale);
  }, [i18nReady, userLocale]);

  if (!i18nReady) return <FullScreenLoader />;

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <I18nextProvider i18n={i18n}>
          <BrowserRouter>
            <Bootstrap />
          </BrowserRouter>
        </I18nextProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
